import React, { useEffect, useState } from 'react';
import { Box, Typography, List, ListItem, ListItemText, IconButton, Grid, Modal, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button, Snackbar } from '@mui/material';
import EditMeetingForm from '../../components/EditMeetingForm';
import { format, parse, compareAsc } from 'date-fns';
import axios from 'axios';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ActionButton from '../../components/ActionButton';

const getEntityName = (entity) => {
  if (!entity) return 'Unknown Entity';
  
  let name = entity.entityName || entity.name;
  
  if (typeof name === 'string') {
    return name.charAt(0).toUpperCase() + name.slice(1);
  }
  
  if (name && typeof name === 'object') {
    if (name.S && typeof name.S === 'string') {
      return name.S.charAt(0).toUpperCase() + name.S.slice(1);
    }
    // If it's an object but doesn't have an 'S' property, try to stringify it
    name = JSON.stringify(name);
  }
  
  // If name is not a string at this point, convert it to one
  name = String(name);
  
  return name.charAt(0).toUpperCase() + name.slice(1) || 'Unknown Entity';
};

const CalendarSubSidebar = ({ selectedEntity, selectedDate, refreshTrigger, ActionButton, onEditEntity, onOpenEditForm }) => {
const userId = localStorage.getItem('userId');
  const [globalAvailability, setGlobalAvailability] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [entityInfo, setEntityInfo] = useState(null);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const weekday = format(selectedDate, 'EEEE').toLowerCase();
  const [expandedMeetings, setExpandedMeetings] = useState({});
  const [deleteConfirmation, setDeleteConfirmation] = useState({ open: false, meeting: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  
  useEffect(() => {
    
    const fetchEntityInfo = async () => {
      try {
        const url = `${process.env.REACT_APP_API_BASE_URL}/api/entityinfo`;
        console.log('Fetching entity info from:', url);
        const response = await axios.get(url, {
          params: {
            userId: userId,
            uniqueId: selectedEntity.uniqueId?.S || selectedEntity.uniqueId
          }
        });
        if (Array.isArray(response.data)) {
          setEntityInfo(response.data);
        } else if (typeof response.data === 'object' && !response.data.hasOwnProperty('DOCTYPE')) {
          // If it's an object but not HTML, we'll treat it as valid data
          setEntityInfo([response.data]);
        } else {
          console.error('Unexpected entity info format:', response.data);
          setEntityInfo(null);
        }
      } catch (error) {
        console.error('Error fetching entity info:', error);
        setEntityInfo(null);
      }
    };

    const fetchGlobalAvailability = async () => {
      try {
        const url = `${process.env.REACT_APP_API_BASE_URL}/api/calendar/get-availability`;
        console.log('Fetching global availability from:', url);
        const response = await axios.get(url, {
          params: { 
            date: format(selectedDate, 'yyyy-MM-dd'),
            userId: userId
          }
        });
        if (Array.isArray(response.data)) {
          setGlobalAvailability(response.data);
        } else if (typeof response.data === 'object' && !response.data.hasOwnProperty('DOCTYPE')) {
          // If it's an object but not HTML, we'll treat it as valid data
          setGlobalAvailability(response.data);
        } else {
          console.error('Unexpected global availability format:', response.data);
          setGlobalAvailability(null);
        }
      } catch (error) {
        console.error('Error fetching global availability:', error);
        setGlobalAvailability(null);
      }
    };

const fetchMeetings = async () => {
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/meetings`;
      console.log('Fetching meetings from:', url);
      if (!userId || !selectedEntity || !selectedEntity.entityName || !selectedEntity.entityNumber || !selectedDate) {
        console.error('Missing userId, entityName, entityNumber, or selectedDate for fetching meetings');
        return;
      }
      const params = {
        userId: userId,
        entityName: selectedEntity.entityName?.S || selectedEntity.entityName,
        entityNumber: selectedEntity.entityNumber?.S || selectedEntity.entityNumber,
        date: format(selectedDate, 'yyyy-MM-dd')
      };
      console.log('Meetings API params:', params);
      const response = await axios.get(url, { params });
      console.log('Meetings API Response:', JSON.stringify(response.data, null, 2));
      if (Array.isArray(response.data.meetings)) {
        setMeetings(response.data.meetings);
      } else if (Array.isArray(response.data)) {
        setMeetings(response.data);
      } else if (typeof response.data === 'object' && !response.data.hasOwnProperty('DOCTYPE')) {
        // If it's an object but not HTML, we'll treat it as valid data
        setMeetings([response.data]);
      } else {
        console.error('Unexpected meetings format:', response.data);
        setMeetings([]);
      }
    } catch (error) {
      console.error('Error fetching meetings:', error);
      setMeetings([]);
    }
  };

    if (selectedEntity && userId && selectedEntity.uniqueId) {
      fetchEntityInfo();
      fetchGlobalAvailability();
      fetchMeetings();
    } else {
      console.log('Not fetching data - missing selectedEntity, userId, or uniqueId');
    }
  }, [selectedDate, selectedEntity, userId, refreshTrigger]);

  const renderAvailability = () => {
    if (!selectedEntity) {
      return <Typography>No entity selected.</Typography>;
    }

    if (!entityInfo) {
      return <Typography>Entity information not available. There might be an issue with the server.</Typography>;
    }

    console.log('Entity Info:', entityInfo);

    const selectedWeekday = format(selectedDate, 'EEEE');
    console.log('Entity Info:', JSON.stringify(entityInfo, null, 2));
    console.log('Selected Weekday:', selectedWeekday);

    let entityDayAvailability = null;
    if (Array.isArray(entityInfo) && entityInfo.length > 0 && entityInfo[0].availability) {
      const weekdayAvailability = entityInfo[0].availability[selectedWeekday];
      if (weekdayAvailability && Array.isArray(weekdayAvailability.L)) {
        entityDayAvailability = weekdayAvailability.L.map(slot => ({
          startTime: slot.M.startTime.S,
          endTime: slot.M.endTime.S,
        }));
      }
    }

    console.log('Entity Day Availability:', JSON.stringify(entityDayAvailability, null, 2));

    const formatTime = (timeString) => {
      if (!timeString) return 'N/A';
      const date = parse(timeString, 'HH:mm', new Date());
      return format(date, 'h:mm a');
    };

    return (
      <Box>
        <List dense disablePadding>
          <ListItem disableGutters>
            <ListItemText
              primary={
                <Box display="flex" alignItems="center" justifyContent="flex-end">
                  <IconButton 
                    onClick={() => {
                      console.log("Edit icon clicked");
                      const entityToEdit = {
                        ...selectedEntity,
                        uniqueId: selectedEntity.uniqueId?.S || selectedEntity.uniqueId
                      };
                      if (onOpenEditForm) {
                        onOpenEditForm(entityToEdit);
                      } else {
                        console.error("onOpenEditForm prop is not defined");
                      }
                    }} 
                    size="small"
                  >
                  </IconButton>
                </Box>
              }
              secondary={
                entityDayAvailability && entityDayAvailability.length > 0
                  ? (
                    <Grid container spacing={1}>
                      {entityDayAvailability
                        .sort((a, b) => {
                          const dateA = parse(a.startTime, 'HH:mm', new Date());
                          const dateB = parse(b.startTime, 'HH:mm', new Date());
                          return compareAsc(dateA, dateB);
                        })
                        .map((slot, index) => (
                          <Grid item xs={6} key={index}>
                            <Typography variant="body2" color="#1976d2">
                              Slot {index + 1}
                            </Typography>
                            <Typography variant="body2" color="#333">
                              {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                            </Typography>
                          </Grid>
                        ))}
                    </Grid>
                  )
                  : "Not available on this day"
              }
              primaryTypographyProps={{ variant: 'subtitle1', color: '#1976d2' }}
              secondaryTypographyProps={{ variant: 'body1', color: '#333' }}
            />
          </ListItem>
        </List>
      </Box>
    );
  };

const renderMeetings = () => {
    if (editingMeeting) {
      return null; // Render nothing if the modal is open
    }

    if (!meetings || meetings.length === 0) {
      return <Typography>No meetings scheduled for this date.</Typography>;
    }

    const formatMeetingTime = (timeString) => {
      if (!timeString) return 'N/A';
      const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
      if (timeRegex.test(timeString)) {
        const date = parse(timeString, 'HH:mm', new Date());
        return format(date, 'h:mm a');
      }
      const date = new Date(timeString);
      return isNaN(date.getTime()) ? 'Invalid Time' : format(date, 'h:mm a');
    };

    const toggleMeetingDetails = (index) => {
      setExpandedMeetings((prevState) => ({
        ...prevState,
        [index]: !prevState[index],
      }));
    };

    return (
      <List dense disablePadding>
        {meetings.map((meeting, index) => (
          <ListItem
            key={index}
            disableGutters
            onClick={() => toggleMeetingDetails(index)}
            style={{
              cursor: 'pointer',
              transition: 'max-height 0.3s ease',
              maxHeight: expandedMeetings[index] ? '500px' : '50px',
              overflow: 'hidden',
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <ListItemText
                primary={`Meet with: ${meeting.meetingWith || 'N/A'}`}
                secondary={
                  <>
                    <Typography variant="body2" color="#333">
                      <strong>Start time:</strong> {formatMeetingTime(meeting.startTime)}
                    </Typography>
                    {expandedMeetings[index] && (
                      <>
                        <Typography variant="body2" color="#333">
                          <strong>End time:</strong> {formatMeetingTime(meeting.endTime)}
                        </Typography>
                        <Typography variant="body2" color="#333">
                          <strong>Meet method:</strong> {meeting.meetMethod || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="#333">
                          <strong>Meet mode:</strong> {meeting.meetMode || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="#333">
                          <strong>Phone:</strong> {meeting.phone || 'N/A'}
                        </Typography>
                      </>
                    )}
                  </>
                }
                primaryTypographyProps={{ variant: 'subtitle2', color: '#1976d2' }}
                secondaryTypographyProps={{ variant: 'body2', color: '#333' }}
              />
              <Box display="flex" alignItems="center">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log("Edit meeting clicked", meeting);
                    setEditingMeeting(meeting);
                    setEditModalOpen(true);
                  }}
                  size="small"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log("Delete meeting clicked", meeting);
                    setDeleteConfirmation({ open: true, meeting: meeting });
                  }}
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <>
      <Box sx={{ width: 550, height: '100%', borderLeft: '1px solid #e0e0e0', overflow: 'auto', p: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
        {selectedEntity ? `${getEntityName(selectedEntity)}'s Schedules` : 'Select an entity'}
      </Typography>
      <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '1.1rem', color: '#333' }}>
        On {format(selectedDate, 'MMMM d, yyyy')} ({format(selectedDate, 'EEEE')})
      </Typography>
      
      {renderAvailability()}
      
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={3} mb={2}>
        <Typography variant="h6" sx={{ color: '#1976d2' }}>Scheduled Meetings</Typography>
      </Box>
      <Box sx={{ 
        maxHeight: 'calc(100vh - 300px)', 
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,.2)',
          borderRadius: '3px',
        },
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(0,0,0,.2) transparent'
      }}>
        {renderMeetings()}
      </Box>
      </Box>
      <Modal
      open={isEditModalOpen}
      onClose={() => {
        setEditModalOpen(false);
        setEditingMeeting(null);
      }}
      aria-labelledby="edit-meeting-modal-title"
      aria-describedby="edit-meeting-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}
      >
        <EditMeetingForm
          meetingData={editingMeeting}
          onSave={async (updatedMeeting) => {
            console.log('Updated meeting:', updatedMeeting);
            if (!updatedMeeting.meetingWith || !updatedMeeting.startTime || !updatedMeeting.endTime || !updatedMeeting.phone) {
              setSnackbar({ open: true, message: 'Please fill all required fields.' });
              return;
            }
            try {
              const url = `${process.env.REACT_APP_API_BASE_URL}/api/meeting`;
              const response = await axios.put(url, {
                entityName: selectedEntity.entityName?.S || selectedEntity.entityName,
                entityNumber: selectedEntity.entityNumber?.S || selectedEntity.entityNumber,
                oldPhone: editingMeeting.phone,
                meetingDate: format(selectedDate, 'yyyy-MM-dd'),
                updates: {
                  meetingWith: updatedMeeting.meetingWith,
                  startTime: updatedMeeting.startTime,
                  endTime: updatedMeeting.endTime,
                  meetMethod: updatedMeeting.meetMethod,
                  meetMode: updatedMeeting.meetMode,
                  phone: updatedMeeting.phone,
                }
              });
              console.log('Edit meeting request payload:', {
                entityName: selectedEntity.entityName?.S || selectedEntity.entityName,
                entityNumber: selectedEntity.entityNumber?.S || selectedEntity.entityNumber,
                phone: updatedMeeting.phone,
                meetingDate: format(selectedDate, 'yyyy-MM-dd'),
                updates: {
                  meetingWith: updatedMeeting.meetingWith,
                  startTime: updatedMeeting.startTime,
                  endTime: updatedMeeting.endTime,
                  meetMethod: updatedMeeting.meetMethod,
                  meetMode: updatedMeeting.meetMode,
                }
              });
              console.log('Edit meeting response:', response.data);
              setEditModalOpen(false);
              setEditingMeeting(null);
              // Refresh meetings after successful edit
              setMeetings((prevMeetings) =>
                prevMeetings.map((m) =>
                  m.meetingId === updatedMeeting.meetingId ? { ...m, ...updatedMeeting } : m
                )
              );
              setSnackbar({ open: true, message: 'Meeting updated successfully.' });
            } catch (error) {
              console.error('Error editing meeting:', error);
              setSnackbar({ open: true, message: 'Error updating meeting. Please try again.' });
            }
          }}
          onCancel={() => {
            setEditModalOpen(false);
            setEditingMeeting(null);
          }}
        />
      </Box>
      </Modal>
      <Dialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, meeting: null })}
        aria-labelledby="delete-confirm-dialog-title"
        aria-describedby="delete-confirm-dialog-description"
      >
        <DialogTitle id="delete-confirm-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-confirm-dialog-description">
            Are you sure you want to delete this meeting? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmation({ open: false, meeting: null })}>
            Cancel
          </Button>
          <Button onClick={async () => {
            try {
              const url = `${process.env.REACT_APP_API_BASE_URL}/api/meeting`;
              const response = await axios.delete(url, {
                data: {
                  meetingWith: deleteConfirmation.meeting.meetingWith,
                  phone: deleteConfirmation.meeting.phone,
                  meetingDate: format(selectedDate, 'yyyy-MM-dd')
                }
              });
              console.log("Delete meeting response:", response.data);
              // Refresh meetings after successful deletion
              setMeetings((prevMeetings) =>
                prevMeetings.filter((m) => m.meetingWith !== deleteConfirmation.meeting.meetingWith || m.phone !== deleteConfirmation.meeting.phone)
              );
            } catch (error) {
              console.error("Error deleting meeting:", error);
            }
            setDeleteConfirmation({ open: false, meeting: null });
          }} variant="contained" color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </>
  );
};

export default CalendarSubSidebar;
